import { useState } from 'react';

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    const formData = new FormData(form);
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'phone_alt') payload.append(key, value as string);
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qg8cnnahb7382ovak0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (!response.ok || parsed?.code !== 'OK' || (serverMsg && serverMsg.toLowerCase().includes('spam'))) {
        setFormError(serverMsg || 'Something went wrong. Please try again.');
        setFormStatus('error');
        return;
      }

      setFormStatus('success');
      form.reset();
    } catch (err) {
      setFormError('Network error. Please check your connection and try again.');
      setFormStatus('error');
    }
  };

  return (
    <section id="contact" className="relative w-full bg-background-50 py-16 md:py-24">
      <div className="container-content">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Left — Info */}
          <div className="lg:w-[40%]">
            <p className="font-label font-medium text-label-lg text-primary-600 tracking-wide uppercase mb-3">
              Get in Touch
            </p>
            <h2 className="font-heading text-display-md text-foreground-950 mb-4 text-balance">
              Have questions? We are here to help.
            </h2>
            <p className="text-body-lg text-foreground-600 leading-relaxed mb-8">
              Whether you need a custom quote, want to discuss a recurring contract, or just have a question about our services — reach out and we will respond within a few hours.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                  <i className="ri-mail-line text-primary-600" />
                </div>
                <div>
                  <p className="font-semibold text-body-sm text-foreground-950">Email</p>
                  <p className="text-body-sm text-foreground-600">hello@cleanupnaija.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                  <i className="ri-phone-line text-primary-600" />
                </div>
                <div>
                  <p className="font-semibold text-body-sm text-foreground-950">Phone</p>
                  <p className="text-body-sm text-foreground-600">+234 800 CLEAN UP</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                  <i className="ri-map-pin-line text-primary-600" />
                </div>
                <div>
                  <p className="font-semibold text-body-sm text-foreground-950">Service Areas</p>
                  <p className="text-body-sm text-foreground-600">Lagos · Abuja · Port Harcourt · Ibadan</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                  <i className="ri-whatsapp-line text-primary-600" />
                </div>
                <div>
                  <p className="font-semibold text-body-sm text-foreground-950">WhatsApp</p>
                  <a
                    href="https://wa.me/2348002000300"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-body-sm text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    Chat with us directly
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="lg:w-[60%]">
            <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200">
              {formStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-primary-600" />
                  </div>
                  <h3 className="font-heading font-semibold text-heading-md text-foreground-950 mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-body-md text-foreground-600">
                    Thank you for reaching out. We will get back to you within a few hours.
                  </p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-4 px-5 py-2 rounded-md text-body-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="" id="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-body-sm font-medium text-foreground-700 mb-1">Full Name</label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-body-sm font-medium text-foreground-700 mb-1">Email</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                        placeholder="you@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-body-sm font-medium text-foreground-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                      placeholder="+234 800 000 0000"
                    />
                  </div>
                  <div>
                    <label className="block text-body-sm font-medium text-foreground-700 mb-1">Subject</label>
                    <select
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                    >
                      <option value="">Select a topic</option>
                      <option>General Inquiry</option>
                      <option>Get a Quote</option>
                      <option>Service Feedback</option>
                      <option>Partnership</option>
                      <option>Careers</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-body-sm font-medium text-foreground-700 mb-1">Message</label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50 resize-none"
                      placeholder="How can we help you?"
                    />
                  </div>
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
                    style={{ clip: 'rect(0,0,0,0)' }}
                  />
                  {formStatus === 'error' && formError && (
                    <div className="flex items-center gap-2 text-body-sm text-red-600 bg-red-50 rounded-md px-3 py-2">
                      <i className="ri-error-warning-line" />
                      {formError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full px-6 py-3 rounded-md text-body-md font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-60"
                  >
                    {formStatus === 'submitting' ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}